import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    ActionButton,
    Content,
    ContextualHelp,
    Flex,
    Heading,
    Item,
    Menu,
    MenuTrigger,
    Section,
    Text,
} from '@adobe/react-spectrum';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Label,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { BaseDeckPane } from './BaseDeckPane';
import { StatMetricTile } from '../../common/cardStats/StatMetricTile';
import { StatsSection } from '../../common/cardStats/StatsSection';
import { formatInt, formatNumber } from '../../common/cardStats/formatters';
import {
    CHART_ACCENT_GOLD,
    CHART_ACCENT_RED,
    CHART_AXIS_STYLE,
    CHART_GRID_STROKE,
    CHART_GRID_STROKE_DASH,
    CHART_INK,
    CHART_INK_MUTED,
    CHART_LABEL_STYLE,
    CHART_LEGEND_WRAPPER_STYLE,
    CHART_MANA_COLORS,
    SEARED_TOOLTIP_CURSOR,
    searedAreaGradient,
    searedLegendFormatter,
    SearedTooltip,
    gradientId,
} from '../../common/cardStats/chartTheme.jsx';
import './manabasePane.css';
import './mana-cost.css';

const CATEGORY_CONSTANTS = {
    ritualScores: { label: 'Rituals' },
    dorkScores: { label: 'Dorks' },
    rockScores: { label: 'Rocks' },
    landScores: { label: 'Lands' },
    treasureScores: { label: 'Treasure' },
    otherScores: { label: 'Other' },
    landRampScores: { label: 'Land Ramp' },
};

const COLOR_SWATCH = {
    c: { label: 'Colorless', fill: CHART_MANA_COLORS.c, pip: 'c' },
    w: { label: 'White', fill: CHART_MANA_COLORS.w, pip: 'w' },
    u: { label: 'Blue', fill: CHART_MANA_COLORS.u, pip: 'u' },
    b: { label: 'Black', fill: CHART_MANA_COLORS.b, pip: 'b' },
    r: { label: 'Red', fill: CHART_MANA_COLORS.r, pip: 'r' },
    g: { label: 'Green', fill: CHART_MANA_COLORS.g, pip: 'g' },
};

const CARD_TYPE_BUCKETS = [
    { key: 'commanders', label: 'Commanders', match: (card) => card?.isCommander },
    {
        key: 'planeswalkers',
        label: 'Planeswalkers',
        match: (card) => !card?.isCommander && (card?.types || '').toString().toLowerCase().includes('planeswalker'),
    },
    {
        key: 'creatures',
        label: 'Creatures',
        match: (card) => !card?.isCommander && (card?.types || '').toString().toLowerCase().includes('creature'),
    },
    {
        key: 'sorceries',
        label: 'Sorceries',
        match: (card) => (card?.types || '').toString().toLowerCase().includes('sorcery'),
    },
    {
        key: 'instants',
        label: 'Instants',
        match: (card) => (card?.types || '').toString().toLowerCase().includes('instant'),
    },
    {
        key: 'artifacts',
        label: 'Artifacts',
        match: (card) =>
            !card?.isCommander &&
            (card?.types || '').toString().toLowerCase().includes('artifact') &&
            !(card?.types || '').toString().toLowerCase().includes('creature'),
    },
    {
        key: 'enchantments',
        label: 'Enchantments',
        match: (card) =>
            !card?.isCommander &&
            (card?.types || '').toString().toLowerCase().includes('enchantment') &&
            !(card?.types || '').toString().toLowerCase().includes('creature'),
    },
    {
        key: 'battles',
        label: 'Battles',
        match: (card) => (card?.types || '').toString().toLowerCase().includes('battle'),
    },
];


const KeyMetric = StatMetricTile;

export function ManabasePane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const manabase = deck?.details?.manabase;
    const breakdown = manabase?.breakdown;
    const production = breakdown?.production?.breakdown;

    // ---- getCategories powers the card-list categories on the left side
    // panel via BaseDeckPane. Each score bucket → list of cards in that role.
    const getCategories = () => {
        const list = {};
        Object.keys(CATEGORY_CONSTANTS)
            .sort((a, b) =>
                CATEGORY_CONSTANTS[a].label.toLowerCase() < CATEGORY_CONSTANTS[b].label.toLowerCase() ? -1 : 1
            )
            .forEach((category) => {
                const bucket = breakdown?.[category];
                if (!bucket || Object.keys(bucket).length === 0) return;
                const items = Object.keys(bucket).map((id) => ({
                    score: parseFloat(bucket[id] || '0.00').toFixed(2),
                    ...cards?.[id],
                }));
                list[category] = {
                    label: CATEGORY_CONSTANTS[category].label,
                    id: category,
                    count: items.length,
                    list: items,
                    color: '#FFFFFF',
                    subCategories: [],
                };
            });
        return list;
    };

    // ---- Per-color mana pip production vs requirement ----
    const colorRows = useMemo(() => {
        if (!production) return [];
        return Object.keys(production).map((color) => {
            const data = production[color] || {};
            const meta = COLOR_SWATCH[color] || { label: color.toUpperCase(), fill: '#999', pip: color };
            const rawReq = parseFloat(data?.percentages?.requirements) || 0;
            const rawProd = parseFloat(data?.percentages?.production) || 0;
            const avgReq = Math.round(rawReq * 100);
            const avgProd = Math.round(rawProd * 100);
            const details = {
                requirementsCount: data?.requirements?.count,
                averageDensity: data?.requirements?.averageDensity,
                productionCount: data?.production?.count,
                averageProduced: data?.production?.averageProduced,
                slowQuantity: data?.production?.details?.slowQuantity,
                basicLands: data?.production?.details?.basicLands,
                fetchableLands: data?.production?.details?.fetchableLands,
                fetchesCount: data?.production?.details?.fetchesCount,
                basicFetchesCount: data?.production?.details?.basicFetchesCount,
                landRampCount: data?.production?.details?.landRampCount,
                producerTypes: data?.production?.types || {},
            };
            return {
                color,
                meta,
                avgReq,
                avgProd,
                avgSources: data?.production?.count || 0,
                details,
            };
        });
    }, [production]);

    // ---- Opening-hand probabilities (per-color + fast mana) ----
    const openingHandData = useMemo(() => {
        const handObject = manabase?.probabilities?.openingHand;
        if (!handObject) return { data: [], colors: [] };
        const colors = Object.keys(handObject?.color || {});
        const data = [];
        for (let i = 0; i < 8; i++) {
            const row = { name: `${i}` };
            colors.forEach((color) => {
                row[color.toUpperCase()] = Math.round((handObject.color[color]?.[i] || 0) * 100);
            });
            row.Fastmana = Math.round((handObject?.fastmana?.[i] || 0) * 100);
            data.push(row);
        }
        return { data, colors };
    }, [manabase?.probabilities?.openingHand]);

    // ---- On-curve castability (actual vs expected by turn) ----
    const curveData = useMemo(() => {
        const turns = manabase?.curveChart?.turns;
        if (!turns) return [];
        const rows = [];
        for (let i = 1; i < 9; i++) {
            const curveObject = turns?.[i];
            if (!curveObject) continue;
            rows.push({
                turn: i,
                Expected: Math.round((parseFloat(curveObject?.baseLinePercentage) || 0) * 100),
                Actual: Math.round((parseFloat(curveObject?.actualPercentage) || 0) * 100),
            });
        }
        return rows;
    }, [manabase?.curveChart?.turns]);

    // ---- Mana value distribution (NEW for deck pane, matches commander) ----
    // Build a nonland card count at each mana value from `cards`.
    const manaValueDistribution = useMemo(() => {
        const counts = new Map();
        Object.values(cards || {}).forEach((card) => {
            if (!card) return;
            const types = (card?.types || '').toString().toLowerCase();
            if (types.includes('land')) return;
            if (card?.isCommander) return;
            if (card?.parseIndividually === false) return;
            const mv = parseInt(card?.manaValue ?? card?.cmc ?? card?.convertedManaCost, 10);
            if (Number.isNaN(mv)) return;
            const key = mv >= 7 ? 7 : mv;
            counts.set(key, (counts.get(key) || 0) + 1);
        });
        const rows = [];
        for (let i = 0; i <= 7; i++) {
            const label = i === 7 ? '7+' : `${i}`;
            rows.push({ manaValue: label, count: counts.get(i) || 0 });
        }
        return rows;
    }, [cards]);

    // ---- Source-type breakdown (NEW for deck pane, matches commander) ----
    // Derived from the score bucket card counts.
    const sourcesData = useMemo(() => {
        const rows = [
            { key: 'lands', label: 'Lands', count: breakdown?.totalManaProducingLandCount || 0 },
            { key: 'rocks', label: 'Mana rocks', count: Object.keys(breakdown?.rockScores || {}).length },
            { key: 'dorks', label: 'Mana dorks', count: Object.keys(breakdown?.dorkScores || {}).length },
            { key: 'rituals', label: 'Rituals', count: Object.keys(breakdown?.ritualScores || {}).length },
            { key: 'treasure', label: 'Treasure', count: Object.keys(breakdown?.treasureScores || {}).length },
            { key: 'landRamp', label: 'Land ramp', count: Object.keys(breakdown?.landRampScores || {}).length },
        ];
        return rows.filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
    }, [breakdown]);

    // ---- Per-card castability dropdown wiring ----
    const cardBuckets = useMemo(() => {
        if (!cards) return [];
        const buckets = new Map();
        CARD_TYPE_BUCKETS.forEach((b) => buckets.set(b.key, { label: b.label, children: [] }));
        Object.values(cards).forEach((card) => {
            if (!card) return;
            if (card?.parseIndividually === false) return;
            const types = (card?.types || '').toString().toLowerCase();
            if (types.includes('land') && !card?.isCommander) return;
            for (const bucket of CARD_TYPE_BUCKETS) {
                if (bucket.match(card)) {
                    buckets.get(bucket.key).children.push({ id: card.id, name: card.name });
                    break;
                }
            }
        });
        return Array.from(buckets.entries())
            .filter(([, b]) => b.children.length > 0)
            .map(([key, b]) => ({
                id: `${key}Cards`,
                name: b.label,
                children: b.children.sort((a, b2) => (a.id < b2.id ? -1 : 1)),
            }));
    }, [cards]);

    const firstCardId = cardBuckets?.[0]?.children?.[0]?.id || null;
    const [dropDownCardId, setDropDownCardId] = useState(firstCardId);
    const effectiveCardId = dropDownCardId || firstCardId;

    const perCardData = useMemo(() => {
        const cardProb = manabase?.probabilities?.cards?.[effectiveCardId];
        if (!cardProb?.turns) return [];
        const rows = [];
        for (let i = 1; i < 9; i++) {
            const turn = cardProb.turns[i];
            if (turn == null) continue;
            const inHand = Math.round((parseFloat(turn?.inHand ?? turn) || 0) * 100);
            rows.push({ turn: i, inHand });
        }
        return rows;
    }, [manabase?.probabilities?.cards, effectiveCardId]);

    // Build MTG color palette for opening-hand area lines.
    const colorLineStroke = (symbol) => CHART_MANA_COLORS[symbol?.toLowerCase()] || '#bfc7da';

    // ---- Render ----
    return (
        <BaseDeckPane
            title="Manabase"
            content={<div />}
            showScore={false}
            categories={getCategories()}
        >
            <Flex direction="column" rowGap={24} marginTop={10}>
                {/* ===== KEY METRICS ===== */}
                <div className="statsMetricsGrid">
                    <KeyMetric
                        label="Lands"
                        value={formatInt(breakdown?.totalManaProducingLandCount)}
                        hint="mana-producing"
                    />
                    <KeyMetric label="Base CMC" value={formatInt(breakdown?.baseTotalCmc)} hint="sum across nonland" />
                    <KeyMetric
                        label="Cost reducers"
                        value={formatInt(Object.keys(breakdown?.genericManaReducers || {}).length)}
                        hint="generic mana reducers"
                    />
                    <KeyMetric
                        label="Reduced cards"
                        value={formatInt(manabase?.details?.totalReducedCostCards)}
                        hint="cards with cost reduction"
                    />
                </div>

                {/* ===== ON-CURVE CASTABILITY ===== */}
                {curveData.length > 0 && (
                    <StatsSection
                        title="On-curve castability"
                        subtitle="Actual vs expected % of cards castable on each turn"
                        helpHeading="Castability by Turn"
                        helpContent={
                            <Flex direction="column" rowGap={8}>
                                <Text>
                                    <b>Expected</b> is how the deck would play if every card were cast on-curve
                                    (2-drops on turn 2, etc.).
                                </Text>
                                <Text>
                                    <b>Actual</b> is the statistical count of cards per turn that are actually
                                    castable given your manabase.
                                </Text>
                            </Flex>
                        }
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={240}>
                                <AreaChart data={curveData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                    <defs>
                                        {searedAreaGradient({
                                            id: gradientId('curve-actual'),
                                            color: CHART_ACCENT_GOLD,
                                        })}
                                        {searedAreaGradient({
                                            id: gradientId('curve-expected'),
                                            color: CHART_INK_MUTED,
                                        })}
                                    </defs>
                                    <CartesianGrid strokeDasharray={CHART_GRID_STROKE_DASH} stroke={CHART_GRID_STROKE} />
                                    <XAxis dataKey="turn" {...CHART_AXIS_STYLE}>
                                        <Label value="Turn" offset={-6} position="insideBottom" style={CHART_LABEL_STYLE} />
                                    </XAxis>
                                    <YAxis
                                        {...CHART_AXIS_STYLE}
                                        domain={[0, 100]}
                                        width={38}
                                        tickFormatter={(v) => `${v}%`}
                                    />
                                    <Tooltip
                                        content={<SearedTooltip labelFormatter={(v) => `Turn ${v}`} valueFormatter={(v) => `${v}%`} />}
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Legend
                                        wrapperStyle={CHART_LEGEND_WRAPPER_STYLE}
                                        iconType="circle"
                                        iconSize={9}
                                        formatter={searedLegendFormatter}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="Expected"
                                        stroke={CHART_INK_MUTED}
                                        strokeWidth={2}
                                        strokeDasharray="4 3"
                                        fill={`url(#${gradientId('curve-expected')})`}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="Actual"
                                        stroke={CHART_ACCENT_GOLD}
                                        strokeWidth={2.5}
                                        fill={`url(#${gradientId('curve-actual')})`}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== MANA VALUE DISTRIBUTION ===== */}
                {manaValueDistribution.some((r) => r.count > 0) && (
                    <StatsSection
                        title="Mana value distribution"
                        subtitle="Nonland card count at each mana value (7+ is bucketed)"
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={manaValueDistribution} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray={CHART_GRID_STROKE_DASH} stroke={CHART_GRID_STROKE} />
                                    <XAxis dataKey="manaValue" {...CHART_AXIS_STYLE}>
                                        <Label value="Mana value" offset={-6} position="insideBottom" style={CHART_LABEL_STYLE} />
                                    </XAxis>
                                    <YAxis {...CHART_AXIS_STYLE} width={38} allowDecimals={false} />
                                    <Tooltip
                                        content={<SearedTooltip labelFormatter={(v) => `MV ${v}`} valueFormatter={(v) => `${v} card${v === 1 ? '' : 's'}`} />}
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Bar dataKey="count" fill={CHART_ACCENT_RED} radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== OPENING HAND PROBABILITIES ===== */}
                {openingHandData.data.length > 0 && openingHandData.colors.length > 0 && (
                    <StatsSection
                        title="Opening hand probabilities"
                        subtitle="% chance of N mana producers of each color (plus fast mana) in a 7-card opener"
                        helpHeading="Pip color percentages"
                        helpContent={
                            <Flex direction="column" rowGap={8}>
                                <Text>
                                    Shows how likely you are to have N mana-producing lands (including fetch lands)
                                    of a given color in your opening hand.
                                </Text>
                                <Text>
                                    The x-axis is the <b>quantity</b> of cards producing that pip. Fast mana is
                                    included as a separate line.
                                </Text>
                            </Flex>
                        }
                    >
                        <div className="manabaseChartWrap">
                            <ResponsiveContainer width="100%" height={300}>
                                <AreaChart data={openingHandData.data} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                    <CartesianGrid strokeDasharray={CHART_GRID_STROKE_DASH} stroke={CHART_GRID_STROKE} />
                                    <XAxis dataKey="name" {...CHART_AXIS_STYLE}>
                                        <Label
                                            value="# of mana producers in opener"
                                            offset={-6}
                                            position="insideBottom"
                                            style={CHART_LABEL_STYLE}
                                        />
                                    </XAxis>
                                    <YAxis
                                        {...CHART_AXIS_STYLE}
                                        domain={[0, 100]}
                                        width={38}
                                        tickFormatter={(v) => `${v}%`}
                                    />
                                    <Tooltip
                                        content={<SearedTooltip labelFormatter={(v) => `${v} source${v === '1' ? '' : 's'}`} valueFormatter={(v) => `${v}%`} />}
                                        cursor={SEARED_TOOLTIP_CURSOR}
                                    />
                                    <Legend
                                        wrapperStyle={CHART_LEGEND_WRAPPER_STYLE}
                                        iconType="circle"
                                        iconSize={9}
                                        formatter={searedLegendFormatter}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="Fastmana"
                                        stroke={CHART_ACCENT_GOLD}
                                        strokeWidth={2}
                                        fillOpacity={0}
                                    />
                                    {openingHandData.colors.map((color) => (
                                        <Area
                                            key={color}
                                            type="monotone"
                                            dataKey={color.toUpperCase()}
                                            stroke={colorLineStroke(color)}
                                            strokeWidth={2.5}
                                            fillOpacity={0}
                                        />
                                    ))}
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </StatsSection>
                )}

                {/* ===== PER-CARD CASTABILITY ===== */}
                {cardBuckets.length > 0 && (
                    <StatsSection
                        title="Card castability"
                        subtitle="Odds of being able to cast a specific card by turn"
                        helpHeading="Card castability"
                        helpContent={
                            <Flex direction="column" rowGap={6}>
                                <Text>Statistical probability of casting this card on turns 1–8, accounting for:</Text>
                                <ul style={{ margin: 0, paddingLeft: 18 }}>
                                    <li>Mana-producing lands (including fetches) of the right pip colors</li>
                                    <li>Pip density per color</li>
                                    <li>Number of distinct colors required relative to turn</li>
                                    <li>Predicted fast mana &amp; ramp availability</li>
                                </ul>
                            </Flex>
                        }
                    >
                        <Flex direction="column" rowGap={12}>
                            <MenuTrigger>
                                <ActionButton UNSAFE_style={{ maxWidth: 320 }}>
                                    {cards?.[effectiveCardId]?.name || 'Pick a card'}
                                </ActionButton>
                                <Menu
                                    items={cardBuckets}
                                    selectedKeys={[effectiveCardId]}
                                    onSelectionChange={(evn) => setDropDownCardId(Array.from(evn)[0])}
                                    selectionMode="single"
                                >
                                    {(bucket) => (
                                        <Section items={bucket.children} title={bucket.name}>
                                            {(card) => <Item key={card.id}>{card.name}</Item>}
                                        </Section>
                                    )}
                                </Menu>
                            </MenuTrigger>
                            {perCardData.length > 0 && (
                                <div className="manabaseChartWrap">
                                    <ResponsiveContainer width="100%" height={220}>
                                        <AreaChart data={perCardData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                                            <defs>
                                                {searedAreaGradient({
                                                    id: gradientId('percard'),
                                                    color: CHART_ACCENT_RED,
                                                })}
                                            </defs>
                                            <CartesianGrid strokeDasharray={CHART_GRID_STROKE_DASH} stroke={CHART_GRID_STROKE} />
                                            <XAxis dataKey="turn" {...CHART_AXIS_STYLE}>
                                                <Label value="Turn" offset={-6} position="insideBottom" style={CHART_LABEL_STYLE} />
                                            </XAxis>
                                            <YAxis
                                                {...CHART_AXIS_STYLE}
                                                domain={[0, 100]}
                                                width={38}
                                                tickFormatter={(v) => `${v}%`}
                                            />
                                            <Tooltip
                                                content={<SearedTooltip labelFormatter={(v) => `Turn ${v}`} valueFormatter={(v) => `${v}%`} />}
                                                cursor={SEARED_TOOLTIP_CURSOR}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="inHand"
                                                name="Castable"
                                                stroke={CHART_ACCENT_RED}
                                                strokeWidth={2.5}
                                                fill={`url(#${gradientId('percard')})`}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Flex>
                    </StatsSection>
                )}

                {/* ===== MANA SOURCE BREAKDOWN ===== */}
                {sourcesData.length > 0 && (
                    <StatsSection
                        title="Mana source breakdown"
                        subtitle="Card count per source category"
                    >
                        <div className="manabaseSourceList">
                            {sourcesData.map((row) => {
                                const max = sourcesData[0]?.count || 1;
                                const pct = Math.max(4, (row.count / max) * 100);
                                return (
                                    <div key={row.key} className="manabaseSourceRow">
                                        <span className="manabaseSourceLabel">{row.label}</span>
                                        <div className="manabaseSourceBarTrack">
                                            <div className="manabaseSourceBarFill" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="manabaseSourceValue">{row.count}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </StatsSection>
                )}

                {/* ===== COLOR PRODUCTION VS REQUIREMENT ===== */}
                {colorRows.length > 0 && (
                    <StatsSection
                        title="Color production vs requirement"
                        subtitle="Red bars = under-producing vs what the deck's spells demand"
                    >
                        <div className="manabaseColorList">
                            {colorRows.map((row) => {
                                const deficit = row.avgProd < row.avgReq;
                                const max = Math.max(row.avgReq, row.avgProd, 1);
                                return (
                                    <div key={row.color} className="manabaseColorRow">
                                        <div className="manabaseColorHeader">
                                            <span className={`mana medium s${row.color}`} />
                                            <span className="manabaseColorName">{row.meta.label}</span>
                                            <span
                                                className={`manabaseColorStatus ${
                                                    deficit ? 'is-deficit' : 'is-healthy'
                                                }`}
                                            >
                                                {deficit ? 'Under' : 'OK'}
                                            </span>
                                        </div>
                                        <div className="manabaseColorBars">
                                            <div className="manabaseColorBarRow">
                                                <span className="manabaseColorBarLabel">Req</span>
                                                <div className="manabaseColorBarTrack">
                                                    <div
                                                        className="manabaseColorBarFill manabaseColorBarFill--req"
                                                        style={{ width: `${(row.avgReq / max) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="manabaseColorBarValue">{row.avgReq}%</span>
                                            </div>
                                            <div className="manabaseColorBarRow">
                                                <span className="manabaseColorBarLabel">Prod</span>
                                                <div className="manabaseColorBarTrack">
                                                    <div
                                                        className="manabaseColorBarFill manabaseColorBarFill--prod"
                                                        style={{
                                                            width: `${(row.avgProd / max) * 100}%`,
                                                            background: row.meta.fill,
                                                        }}
                                                    />
                                                </div>
                                                <span className="manabaseColorBarValue">{row.avgProd}%</span>
                                            </div>
                                        </div>
                                        <ColorStatsPanel row={row} />
                                    </div>
                                );
                            })}
                        </div>
                    </StatsSection>
                )}
            </Flex>
        </BaseDeckPane>
    );
}

/**
 * Deep-dive stats panel for a single color — compact list of all the
 * sub-counters the deck API returns (pip quantity, density, fetches,
 * ramp, etc.). Renders inside the color row so it's discoverable but
 * doesn't dominate the visual weight.
 */
function ColorStatsPanel({ row }) {
    const d = row?.details || {};
    const entries = [
        { label: 'Pip quantity', value: d.requirementsCount },
        { label: 'Avg density', value: d.averageDensity },
        { label: 'Avg produced', value: d.averageProduced },
        { label: 'Slow producers', value: d.slowQuantity },
        { label: 'Basics', value: d.basicLands },
        { label: 'Fetchable', value: d.fetchableLands },
        { label: 'Fetches', value: d.fetchesCount },
        { label: 'Basic fetches', value: d.basicFetchesCount },
        { label: 'Land ramp', value: d.landRampCount },
    ].filter((e) => e.value != null && e.value !== '' && e.value !== 0);

    const producerTypes = Object.entries(d.producerTypes || {}).filter(([, v]) => v);

    if (entries.length === 0 && producerTypes.length === 0) return null;

    return (
        <div className="manabaseColorDetailGrid">
            {entries.map((e) => (
                <div key={e.label} className="manabaseColorDetailCell">
                    <span className="manabaseColorDetailLabel">{e.label}</span>
                    <span className="manabaseColorDetailValue">{e.value}</span>
                </div>
            ))}
            {producerTypes.map(([type, v]) => (
                <div key={`type_${type}`} className="manabaseColorDetailCell">
                    <span className="manabaseColorDetailLabel">
                        {type.charAt(0).toUpperCase()}
                        {type.slice(1)}
                    </span>
                    <span className="manabaseColorDetailValue">{v}</span>
                </div>
            ))}
        </div>
    );
}
